import React from 'react';
import { KatAccordion, KatAccordionItem } from '@amzn/katal-react';
import { StatusPillV2 } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, CREDENTIAL_STRING_ID } from 'src/constants/localization';
import { CREDENTIAL_COMING_SOON_SECTIONS } from 'src/constants/credentialComingSoon';

export interface ComingSoonSectionsProps {
  /** Credential whose configured future sections should render as disabled teasers. */
  credentialId: string;
}

/**
 * Renders a credential's configured future course sections as disabled teasers below its live
 * section. Each reuses the same KatAccordionItem chrome as the live section headers so the layout
 * matches by construction, but with the toggle suppressed, no expandable body, and interaction
 * disabled (pointer-events-none) to signal it is not yet available.
 */
export const ComingSoonSections: React.FC<ComingSoonSectionsProps> = ({ credentialId }) => {
  const [credentialBundle] = useBundle(BUNDLE_NAME.CREDENTIAL);
  const sections = CREDENTIAL_COMING_SOON_SECTIONS[credentialId] ?? [];

  if (sections.length === 0) {
    return null;
  }

  return (
    <KatAccordion className="mx-auto w-full">
      {sections.map((section) => (
        <KatAccordionItem key={section.titleStringId} className="pointer-events-none">
          <div
            slot="label"
            className="flex flex-col gap-su-space-01 p-su-space-05"
          >
            <div className="flex items-center gap-su-space-03">
              <b className="text-su-heading-500 text-su-text-disabled">
                {t(credentialBundle, section.titleStringId)}
              </b>
              <StatusPillV2
                status="outlined"
                label={t(credentialBundle, CREDENTIAL_STRING_ID.COMING_SOON_PILL)}
              />
            </div>
            <span className="text-su-text-disabled">
              {t(credentialBundle, section.descriptionStringId)}
            </span>
          </div>
          <span slot="indicator" />
        </KatAccordionItem>
      ))}
    </KatAccordion>
  );
};

export default ComingSoonSections;
